import {
  Box,
  Button,
  Card,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import { Delete, DocumentScanner, ViewAgenda } from "@mui/icons-material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { CopiableChip } from "../../../../components/ui/CopiableChip";
import { useGetLocale, useModal } from "@refinedev/core";
import { CancelShipmentDialog } from "./CancelShipmentDialog";
import { ShippingLabelModal } from "../prepare-shipment/ShippingLabelModal";
import { OrderStatus, Shipment } from "../../types";

type Props = {
  shipment: Shipment;
  barcode: string;
  orderStatus: OrderStatus;
};

const LABEL_WIDTH = 160;

export const ShipmentRow = ({ shipment, barcode, orderStatus }: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orderDetails.shipmentRow"
  );
  const locale = useGetLocale()();

  const cancelModal = useModal();
  const labelModal = useModal();

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: (theme) => theme.palette.customBackground.box,
        borderLeft: "3px solid",
        borderColor: "primary.main",
      }}
    >
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {translate("shipmentTitle")} {shipment.id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(shipment.createdAt).toLocaleDateString(locale)}
        </Typography>
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* CONTENT */}
      <Grid2 container>
        {/* LEFT – INFO */}
        <Grid2 size={{ md: 12, lg: 8 }}>
          <Stack spacing={1}>
            <Box display="flex">
              <Box width={{ xs: 120, md: LABEL_WIDTH }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {translate("orderInfo.carrierName")}
                </Typography>
              </Box>
              <Typography variant="body2">
                {shipment.carrierName ?? "-"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center">
              <Box width={{ xs: 120, md: LABEL_WIDTH }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {translate("orderInfo.trackingNumber")}
                </Typography>
              </Box>
              <CopiableChip initialLabel={shipment.trackingNumber} />
            </Box>

            <Box display="flex">
              <Box width={{ xs: 120, md: LABEL_WIDTH }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                >
                  {translate("orderInfo.selectedCargoRate")}
                </Typography>
              </Box>
              <Typography variant="body2">{shipment.rate ?? "-"}</Typography>
            </Box>
          </Stack>
        </Grid2>

        {/* RIGHT – ACTIONS */}
        <Grid2 size={{ md: 12, lg: 4 }}>
          <Box>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  startIcon={<ViewAgenda />}
                  onClick={labelModal.show}
                  sx={{ minHeight: 32, py: 0.5, fontSize: 13 }}
                >
                  {translate("orderInfo.label")}
                </Button>

                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  startIcon={<DocumentScanner />}
                  href={shipment.invoiceUrl}
                  target="_blank"
                  sx={{ minHeight: 32, py: 0.5, fontSize: 13 }}
                >
                  {translate("orderInfo.invoice")}
                </Button>
              </Stack>

              {/* BOTTOM – CANCEL */}
              {orderStatus !== "SHIPPED" && (
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete fontSize="small" />}
                  onClick={cancelModal.show}
                  sx={{ minHeight: 30, py: 0.25, fontSize: 12.5 }}
                >
                  {translateWithoutPrefix("buttons.cancel")}
                </Button>
              )}
            </Stack>
          </Box>
        </Grid2>
      </Grid2>

      {/* MODALS */}
      <CancelShipmentDialog
        open={cancelModal.visible}
        onClose={cancelModal.close}
        orderBarcode={barcode}
        id={shipment.id}
      />

      <ShippingLabelModal
        open={labelModal.visible}
        onClose={labelModal.close}
        trackingNumber={shipment.trackingNumber}
        labelUrl={shipment.labelUrl}
        carrierName={shipment.carrierName}
      />
    </Card>
  );
};
